import { useState } from "react";
import { AnimatePresence } from "framer-motion";
import { MessageCircle } from "lucide-react";
import PageHeader from "../components/PageHeader";
import Reveal from "../components/Reveal";
import PackageCard from "../components/PackageCard";
import PackageModal from "../components/PackageModal";
import { BUSINESS, DOMESTIC_PACKAGES, waLink } from "../data/packages";

const REGIONS = ["All Regions", ...new Set(DOMESTIC_PACKAGES.map((p) => p.region))];

export default function DomesticPackages() {
  const [region, setRegion] = useState("All Regions");
  const [sel, setSel] = useState(null);

  const list = region === "All Regions" ? DOMESTIC_PACKAGES : DOMESTIC_PACKAGES.filter((p) => p.region === region);

  return (
    <div data-testid="page-domestic-packages">
      <PageHeader
        testid="domestic-header"
        eyebrow="Domestic Tour Packages"
        title="Explore India's sacred &"
        accent="celestial terrains"
        description="From high-altitude deserts to quiet backwaters — handcrafted routes across the subcontinent, refined over hundreds of departures."
      />

      <Reveal className="mx-auto max-w-7xl px-6">
        <div
          data-testid="booking-strip"
          className="flex flex-col gap-4 rounded-3xl border border-aurora/25 bg-aurora/5 p-6 sm:flex-row sm:items-center sm:justify-between"
        >
          <div className="flex items-center gap-4">
            <MessageCircle className="h-6 w-6 shrink-0 text-aurora" />
            <p className="text-sm leading-relaxed text-slate-300">
              <span className="font-semibold text-slate-100">Booking is simple —</span> WhatsApp us your trip, pay
              the advance via UPI, pack your bags.
            </p>
          </div>
          <a
            data-testid="booking-strip-whatsapp-btn"
            href={waLink(`Hi ${BUSINESS.name}! I want to know more about your domestic trips.`)}
            target="_blank"
            rel="noreferrer"
            className="shrink-0 rounded-full bg-aurora px-6 py-3 text-center text-xs font-bold text-ink transition-all duration-300 hover:shadow-[0_0_30px_rgba(0,245,212,0.4)]"
          >
            Chat on WhatsApp
          </a>
        </div>
      </Reveal>

      <section className="mx-auto max-w-7xl px-6 pb-24 md:pb-32">
        <Reveal>
          <div className="flex flex-wrap gap-2.5" data-testid="region-filter">
            {REGIONS.map((r) => (
              <button
                key={r}
                data-testid={`filter-chip-${r.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                onClick={() => setRegion(r)}
                className={`rounded-full border px-5 py-2.5 text-xs font-semibold tracking-wide transition-all duration-300 ${
                  region === r
                    ? "border-aurora bg-aurora text-ink"
                    : "border-white/15 text-slate-300 hover:border-aurora/50 hover:text-aurora"
                }`}
              >
                {r}
              </button>
            ))}
          </div>
        </Reveal>

        <div className="mt-12 grid gap-7 sm:grid-cols-2 lg:grid-cols-3" data-testid="domestic-grid">
          {list.map((pkg, i) => (
            <Reveal key={pkg.name} delay={(i % 3) * 0.08}>
              <PackageCard pkg={pkg} onOpen={setSel} testid={`domestic-card-${pkg.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} />
            </Reveal>
          ))}
        </div>

        {list.length === 0 && (
          <p className="py-20 text-center font-editorial text-xl italic text-slate-500">
            No routes in this region yet — ask us for a customised one.
          </p>
        )}
      </section>

      <AnimatePresence>
        {sel && <PackageModal pkg={sel} onClose={() => setSel(null)} tripType="Domestic Package" />}
      </AnimatePresence>
    </div>
  );
}
